import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';

import { carousel1, carousel2, carousel3 } from '../../../../assets/figma';
import { mergeSx } from '../../../../theme';

import { carouselStyles as styles } from './Carousel.styles';

interface CarouselSlide {
  heading: string;
  body: string;
  image: string;
}

const slides: CarouselSlide[] = [
  {
    heading: 'Grant children the gift of a lifetime.',
    body: 'Section 530A created the first federal framework for children\u2019s savings accounts to receive contributions from philanthropy, government, employers, families, and individuals. Discover how you can support children in investing for the long term.',
    image: carousel1,
  },
  {
    heading: 'Target your impact.',
    body: 'Many wealth-building programs struggle to reach working-class families. Explore how you can target your impact to ensure your contributions to 530A accounts reach the families who need it most.',
    image: carousel2,
  },
  {
    heading: 'Compound wealth.',
    body: 'Compound growth is one of the most powerful forces in personal finance. When a child’s 530A account is seeded early, even a small philanthropic contribution has decades to grow - turning a modest gift into a meaningful foundation.',
    image: carousel3,
  },
];

/**
 * Carousel section \u2014 cycles through slides via the dots or the arrow.
 * Figma: carousel sections 386:1583 / 386:1606 / 386:1630.
 */
const N = slides.length;
// One clone of the first slide appended so "next" past the last slide keeps
// sliding forward into it, then we snap back to the real first slide.
const loopSlides = [...slides, slides[0]];

export function Carousel() {
  // `pos` runs 0..N (N = the appended clone); `animate` toggles the transition
  // off for the invisible snap-back.
  const [pos, setPos] = useState(0);
  const [animate, setAnimate] = useState(true);
  const activeIndex = pos % N;
  const active = slides[activeIndex];

  const goToNext = () => {
    setAnimate(true);
    setPos((p) => (p >= N ? p : p + 1));
  };
  const goToDot = (index: number) => {
    setAnimate(true);
    setPos(index);
  };

  // When the slide into the clone finishes, jump (without animation) back to
  // the real first slide, then re-enable the transition on the next frame.
  const handleTransitionEnd = (event: React.TransitionEvent) => {
    if (event.target !== event.currentTarget) return;
    if (pos === N) {
      setAnimate(false);
      setPos(0);
    }
  };
  useEffect(() => {
    if (animate) return;
    const id = requestAnimationFrame(() =>
      requestAnimationFrame(() => setAnimate(true)),
    );
    return () => cancelAnimationFrame(id);
  }, [animate]);

  return (
    <Box component="section" sx={styles.root} aria-labelledby="carousel-heading">
      <Box sx={styles.graphicWrap}>
        <Box
          sx={styles.track}
          style={{
            transform: `translateX(-${pos * 100}%)`,
            transition: animate ? undefined : 'none',
          }}
          onTransitionEnd={handleTransitionEnd}
        >
          {loopSlides.map((slide, index) => (
            <Box
              key={index}
              component="img"
              src={slide.image}
              alt=""
              aria-hidden={index % N !== activeIndex}
              sx={styles.slideImg}
            />
          ))}
        </Box>
      </Box>
      <Box sx={styles.copy}>
        <Typography id="carousel-heading" component="h2" sx={styles.heading}>
          {active.heading}
        </Typography>
        <Typography component="p" sx={styles.body}>
          {active.body}
        </Typography>
        <Box sx={styles.controls}>
          <Box sx={styles.dots} role="tablist" aria-label="Carousel slides">
            {slides.map((slide, index) => (
              <Box
                key={slide.heading}
                component="button"
                type="button"
                role="tab"
                aria-selected={index === activeIndex}
                aria-label={`Slide ${index + 1}`}
                onClick={() => goToDot(index)}
                sx={mergeSx(styles.dot, index === activeIndex && styles.dotActive)}
              />
            ))}
          </Box>
          <Button
            sx={styles.nextButton}
            aria-label="Next slide"
            onClick={goToNext}
          >
            <ArrowForwardIcon fontSize="small" />
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
